import { useCallback } from "react"
import { useMutation } from "urql"

import type { RevertPriceChangeSubmissionInput, RevertPriceChangeSubmissionMutation } from "@/graphql/codegen/graphql"
import { RevertPriceChangeSubmissionDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

import "@/screens/Pricing/graphql/mutations"

interface UseRevertPriceChangeSubmissionReturn {
  revertPriceChangeSubmission: (input: RevertPriceChangeSubmissionInput) => Promise<string | undefined>
  isReverting: boolean
}

export const useRevertPriceChangeSubmission = (): UseRevertPriceChangeSubmissionReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ fetching }, revertPriceChange] = useMutation<
    RevertPriceChangeSubmissionMutation,
    { input: RevertPriceChangeSubmissionInput }
  >(RevertPriceChangeSubmissionDocument)

  const revertPriceChangeSubmission = useCallback(
    async (input: RevertPriceChangeSubmissionInput): Promise<string | undefined> => {
      const { data, error } = await revertPriceChange({ input })
      const output = data?.priceChange.revertPriceChangeSubmission
      const hasError = error || output?.__typename === "RevertPriceChangeSubmissionFailure"

      if (hasError) {
        const message =
          output?.__typename === "RevertPriceChangeSubmissionFailure" ? output.error.message : error?.message
        notify({
          type: NotificationType.Error,
          message: message ?? "Failed to revert price changes. Please try again.",
        })
        return undefined
      }

      notify({ type: NotificationType.Success, message: "Price changes reverted." })

      return output?.__typename === "RevertPriceChangeSubmissionSuccess" ? output.priceChangeSubmission.id : undefined
    },
    [notify, revertPriceChange]
  )

  return { revertPriceChangeSubmission, isReverting: fetching }
}
